import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Provider } from 'react-redux'
import { Toaster } from 'react-hot-toast'
import { configureStore } from '@reduxjs/toolkit'
import createSagaMiddleware from 'redux-saga'
import { all } from 'redux-saga/effects'
import App from './App'
import './index.css'

// Import reducers
import authReducer from './store/reducers/authReducer'
import projectReducer from './store/reducers/projectReducer'
import uiReducer from './store/reducers/uiReducer'

// Import sagas
import { watchAuthSagas } from './store/sagas/authSagas'
import { watchProjectSagas } from './store/sagas/projectSagas'

console.log('=== Initializing Redux Store ===')

// Root saga
function* rootSaga() {
  yield all([
    watchAuthSagas(),
    watchProjectSagas(),
  ])
}

// Create saga middleware
const sagaMiddleware = createSagaMiddleware()

// Create store with Redux Toolkit
const store = configureStore({
  reducer: {
    auth: authReducer as any,
    projects: projectReducer as any,
    ui: uiReducer as any,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      thunk: false,
      serializableCheck: false,
    }).concat(sagaMiddleware),
})

console.log('=== Store created ===')

// Run saga
sagaMiddleware.run(rootSaga)
console.log('=== Sagas started ===')

// Create QueryClient
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: (failureCount, error: any) => {
        if (error?.status === 401) return false
        return failureCount < 3
      },
    },
  },
})

console.log('=== QueryClient created ===')

const root = document.getElementById('root')

if (root) {
  console.log('=== Rendering full app ===')
  ReactDOM.createRoot(root).render(
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <App />
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#363636',
                color: '#fff',
              },
              success: {
                duration: 3000,
                iconTheme: {
                  primary: '#22c55e',
                  secondary: '#fff',
                },
              },
              error: {
                duration: 5000,
                iconTheme: {
                  primary: '#ef4444',
                  secondary: '#fff',
                },
              },
            }}
          />
        </BrowserRouter>
      </QueryClientProvider>
    </Provider>,
  )
  console.log('=== App rendered successfully! ===')
}

export type RootState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch